import React from "react";
import { Box, Typography, Alert, Stack, Chip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

interface SuiteLogProps {
  suiteId?: number | string;
}

/**
 * Consolidated suite-level logs are not produced by the backend yet — there is
 * no `/suite/<id>/log` endpoint. Rather than show fabricated text, we surface an
 * honest empty state that points to where execution detail actually lives
 * (per-case reports / Allure). Wire `logContent` to a real endpoint when one exists.
 */
const SuiteLog: React.FC<SuiteLogProps> = ({ suiteId }) => {
  return (
    <Box sx={{ p: 1 }}>
      <Stack
        sx={{ alignItems: "center", textAlign: "center", py: 4, gap: 2 }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.level2",
            color: "text.secondary",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
            No consolidated log for this suite
          </Typography>
          {suiteId !== undefined && (
            <Chip
              size="small"
              label={`Suite #${suiteId}`}
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Alert severity="info" sx={{ maxWidth: 520, textAlign: "left" }}>
          A suite-wide log stream isn't generated yet. Per-test execution detail
          lives in each case's report — open a case and use{" "}
          <strong>Download result</strong>, or build the{" "}
          <strong>Allure</strong> report from the suite row for a full,
          browsable run report.
        </Alert>
      </Stack>
    </Box>
  );
};

export default SuiteLog;
